import React, { useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  ReferenceField,
  Show,
  SimpleShowLayout,
  SelectInput,
  RichTextField,
  useRecordContext,
  useNotify,
  useRefresh,
  useDataProvider,
  Button,
  TopToolbar,
  ExportButton,
  ShowButton,
  DeleteButton,
  BulkDeleteButton,
  FunctionField,
  SearchInput,
  useUpdate,
  Filter,
  SimpleList,
} from 'react-admin';
import {
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button as MuiButton,
  IconButton,
  Tooltip,
  Divider,
  Paper,
  useMediaQuery,
} from '@mui/material';
import {
  HelpOutline as InquiryIcon,
  LocalShipping as DeliveryIcon,
  SwapHoriz as ExchangeIcon,
  Payment as PaymentIcon,
  Category as OtherIcon,
  Pending as PendingIcon,
  CheckCircle as AnsweredIcon,
  PauseCircle as OnHoldIcon,
  PriorityHigh as UrgentIcon,
  KeyboardArrowUp as HighIcon,
  Remove as NormalIcon,
  KeyboardArrowDown as LowIcon,
  Reply as ReplyIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ShoppingCart as OrderIcon,
  Inventory as ProductIcon,
} from '@mui/icons-material';

// 필터링 옵션
const InquiryFilter = (props: any) => (
  <Filter {...props}>
    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
      <Grid item xs={12} md={6}>
        <SearchInput source="q" alwaysOn placeholder="제목, 작성자명, 이메일 검색" fullWidth />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SelectInput
          source="type"
          label="카테고리"
          choices={[
            { id: 'PRODUCT', name: '상품 문의' },
            { id: 'ORDER', name: '주문 문의' },
            { id: 'DELIVERY', name: '배송 문의' },
            { id: 'RETURN', name: '교환/반품' },
            { id: 'PAYMENT', name: '결제 문의' },
            { id: 'OTHER', name: '기타' },
          ]}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SelectInput
          source="status"
          label="상태"
          choices={[
            { id: 'PENDING', name: '대기중' },
            { id: 'IN_PROGRESS', name: '처리중' },
            { id: 'ANSWERED', name: '답변완료' },
            { id: 'CLOSED', name: '종료' },
          ]}
          fullWidth
        />
      </Grid>
    </Grid>
  </Filter>
);

// 카테고리 필드 컴포넌트
const CategoryField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  if (!record) return null;

  const category = record[source];
  let icon, color, label;

  switch (category) {
    case 'PRODUCT':
      icon = <InquiryIcon sx={{ fontSize: 16 }} />;
      color = 'primary';
      label = '상품 문의';
      break;
    case 'ORDER':
      icon = <OrderIcon sx={{ fontSize: 16 }} />;
      color = 'secondary';
      label = '주문 문의';
      break;
    case 'DELIVERY':
      icon = <DeliveryIcon sx={{ fontSize: 16 }} />;
      color = 'info';
      label = '배송 문의';
      break;
    case 'RETURN':
      icon = <ExchangeIcon sx={{ fontSize: 16 }} />;
      color = 'warning';
      label = '교환/반품';
      break;
    case 'PAYMENT':
      icon = <PaymentIcon sx={{ fontSize: 16 }} />;
      color = 'success';
      label = '결제 문의';
      break;
    default:
      icon = <OtherIcon sx={{ fontSize: 16 }} />;
      color = 'default';
      label = '기타';
  }

  return (
    <Chip
      icon={icon}
      label={label}
      color={color as any}
      size="small"
      variant="outlined"
    />
  );
};

// 상태 필드 컴포넌트
const StatusField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  if (!record) return null;

  const status = record[source];
  let icon, color, label;

  switch (status) {
    case 'ANSWERED':
      icon = <AnsweredIcon sx={{ fontSize: 16 }} />;
      color = 'success';
      label = '답변완료';
      break;
    case 'IN_PROGRESS':
      icon = <OnHoldIcon sx={{ fontSize: 16 }} />;
      color = 'info';
      label = '처리중';
      break;
    case 'CLOSED':
      icon = <OnHoldIcon sx={{ fontSize: 16 }} />;
      color = 'default';
      label = '종료';
      break;
    default:
      icon = <PendingIcon sx={{ fontSize: 16 }} />;
      color = 'warning';
      label = '대기중';
  }

  return (
    <Chip
      icon={icon}
      label={label}
      color={color as any}
      size="small"
      variant="outlined"
    />
  );
};

// 우선순위 필드 컴포넌트
const PriorityField = ({ source }: { source: string }) => {
  const record = useRecordContext();
  if (!record) return null;

  const priority = record[source];
  let icon, color, label;

  switch (priority) {
    case 'URGENT':
      icon = <UrgentIcon sx={{ fontSize: 16 }} />;
      color = 'error';
      label = '긴급';
      break;
    case 'HIGH':
      icon = <HighIcon sx={{ fontSize: 16 }} />;
      color = 'warning';
      label = '높음';
      break;
    case 'LOW':
      icon = <LowIcon sx={{ fontSize: 16 }} />;
      color = 'info';
      label = '낮음';
      break;
    default:
      icon = <NormalIcon sx={{ fontSize: 16 }} />;
      color = 'default';
      label = '보통';
  }

  return (
    <Chip
      icon={icon}
      label={label}
      color={color as any}
      size="small"
      variant="outlined"
    />
  );
};

// 답변 작성 다이얼로그 래퍼 (record context 사용)
const AnswerDialogWrapper = ({ open, onClose }: {
  open: boolean;
  onClose: () => void;
}) => {
  const record = useRecordContext();

  if (!record) return null;

  return <AnswerDialog open={open} onClose={onClose} inquiryId={record.id} />;
};

// 답변 작성 다이얼로그 컴포넌트
const AnswerDialog = ({ open, onClose, inquiryId }: {
  open: boolean;
  onClose: () => void;
  inquiryId: string;
}) => {
  const [answer, setAnswer] = useState('');
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();

  const handleSubmit = async () => {
    if (!answer.trim()) {
      notify('답변 내용을 입력해주세요', { type: 'warning' });
      return;
    }

    try {
      await update('inquiries', {
        id: inquiryId,
        data: {
          answer,
          status: 'ANSWERED',
          answeredAt: new Date().toISOString(),
        },
        previousData: { id: inquiryId }
      });
      notify('답변이 등록되었습니다', { type: 'success' });
      refresh();
      onClose();
      setAnswer('');
    } catch (error: any) {
      console.error('답변 등록 오류:', error);
      notify(error?.message || '답변 등록 중 오류가 발생했습니다', { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>문의 답변 작성</DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <textarea
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder="고객에게 전달될 답변을 작성해주세요."
          rows={8}
          style={{
            width: '100%',
            padding: '12px',
            fontSize: '14px',
            fontFamily: 'inherit',
            border: '1px solid #ccc',
            borderRadius: '4px',
            resize: 'vertical',
          }}
        />
      </DialogContent>
      <DialogActions>
        <MuiButton onClick={onClose}>취소</MuiButton>
        <MuiButton
          onClick={handleSubmit}
          variant="contained"
          disabled={!answer.trim()}
        >
          답변 등록
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// 첨부파일 표시 컴포넌트
const AttachmentsField = () => {
  const record = useRecordContext();
  if (!record?.attachments || record.attachments.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {record.attachments.map((file: any, index: number) => (
        <Chip
          key={index}
          label={file.name || `첨부파일 ${ index + 1 } `}
          size="small"
          variant="outlined"
          onClick={() => window.open(file.url, '_blank')}
          sx={{ cursor: 'pointer' }}
        />
      ))}
    </Box>
  );
};

// 일괄 처리 버튼들
const BulkAnswerButton = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const handleBulkAnswer = async (selectedIds: any[]) => {
    try {
      await dataProvider.updateMany('inquiries', {
        ids: selectedIds,
        data: {
          status: 'ANSWERED',
          answer: '일괄 처리된 답변입니다.',
          answeredBy: '관리자',
          answeredAt: new Date().toISOString(),
        }
      });
      notify('선택된 문의가 일괄 답변 처리되었습니다', { type: 'success' });
      refresh();
    } catch (error) {
      notify('일괄 답변 처리 중 오류가 발생했습니다', { type: 'error' });
    }
  };

  return (
    <Button
      label="일괄 답변"
      onClick={handleBulkAnswer}
      sx={{ backgroundColor: 'success.main' }}
    />
  );
};

const BulkOnHoldButton = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const handleBulkOnHold = async (selectedIds: any[]) => {
    try {
      await dataProvider.updateMany('inquiries', {
        ids: selectedIds,
        data: { status: 'ON_HOLD' }
      });
      notify('선택된 문의가 보류 처리되었습니다', { type: 'warning' });
      refresh();
    } catch (error) {
      notify('보류 처리 중 오류가 발생했습니다', { type: 'error' });
    }
  };

  return (
    <Button
      label="일괄 보류"
      onClick={handleBulkOnHold}
      sx={{ backgroundColor: 'warning.main' }}
    />
  );
};

const InquiryListActions = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);

// 문의 목록
export const InquiryList = () => {
  const isSmall = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));

  return (
    <>
      <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1C1C1E', fontSize: { xs: '1.5rem', md: '2rem' } }}>
          문의
        </Typography>
      </Box>
      <List
        title="문의"
        filters={<InquiryFilter />}
        actions={<InquiryListActions />}
        perPage={25}
        sort={{ field: 'createdAt', order: 'DESC' }}
      >
        {isSmall ? (
          <SimpleList
            primaryText={(record) => (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {record.title}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  <CategoryField source="type" />
                  <StatusField source="status" />
                  {record.priority === 'URGENT' && <PriorityField source="priority" />}
                </Box>
              </Box>
            )}
            secondaryText={(record) => (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {record.email || '작성자 미확인'} | {new Date(record.createdAt).toLocaleDateString()}
                </Typography>
                {record.attachments?.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    📎 첨부파일 {record.attachments.length}개
                  </Typography>
                )}
              </Box>
            )}
            linkType="show"
          />
        ) : (
          <Datagrid
            rowClick="show"
            bulkActionButtons={
              <>
                <BulkAnswerButton />
                <BulkOnHoldButton />
                <BulkDeleteButton />
              </>
            }
          >
            <FunctionField
              label="제목"
              render={(record: any) => (
                <Box sx={{ maxWidth: 250 }}>
                  <Typography variant="body2" fontWeight="medium" sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {record.title}
                  </Typography>
                  {record.attachments?.length > 0 && (
                    <Chip
                      label={`첨부 ${ record.attachments.length } 개`}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              )}
            />
            <FunctionField
              label="카테고리"
              render={(record: any) => (
                <CategoryField source="type" />
              )}
            />
            <ReferenceField source="userId" reference="users" label="작성자" link={false}>
              <FunctionField render={(userRecord: any) => (
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {userRecord?.name || '-'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {userRecord?.email || '-'}
                  </Typography>
                </Box>
              )} />
            </ReferenceField>
            <FunctionField
              label="상태"
              render={(record: any) => (
                <StatusField source="status" />
              )}
            />
            <FunctionField
              label="우선순위"
              render={(record: any) => (
                <PriorityField source="priority" />
              )}
            />
            <ReferenceField source="productId" reference="products" label="관련 상품" link={false}>
              <TextField source="name" />
            </ReferenceField>
            <ReferenceField source="orderId" reference="orders" label="주문번호" link={false}>
              <TextField source="orderNumber" />
            </ReferenceField>
            <DateField source="createdAt" label="작성일" />
            <DateField source="answeredAt" label="답변일" />
            <ShowButton />
            <DeleteButton />
          </Datagrid>
        )}
      </List>
    </>
  );
};

// 문의 상세 보기 내부 컴포넌트
const InquiryShowContent = ({
  answerDialogOpen,
  setAnswerDialogOpen
}: {
  answerDialogOpen: boolean;
  setAnswerDialogOpen: (open: boolean) => void;
}) => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <>
      <SimpleShowLayout sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>문의 상세 정보</Typography>

                {/* 작성일 및 상품 정보 */}
                <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">작성일:</Typography>
                    <Typography variant="body2"><DateField source="createdAt" showTime /></Typography>
                  </Box>
                  {record?.productId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <ProductIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        상품:
                      </Typography>
                      <ReferenceField source="productId" reference="products" link="show">
                        <FunctionField render={(product: any) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {product.thumbnail && (
                              <Box
                                component="img"
                                src={product.thumbnail}
                                alt={product.name}
                                sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 1 }}
                              />
                            )}
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'primary.main' }}>
                              {product.name}
                            </Typography>
                          </Box>
                        )} />
                      </ReferenceField>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" gutterBottom>
                    <TextField source="title" />
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <CategoryField source="type" />
                    <StatusField source="status" />
                    <PriorityField source="priority" />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon /> 문의자 정보
                  </Typography>
                  <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" />
                        {record?.userId ? (
                          <ReferenceField source="userId" reference="users" link={false}>
                            <FunctionField render={(userRecord: any) => (
                              <Typography variant="body2">{userRecord?.name || '-'}</Typography>
                            )} />
                          </ReferenceField>
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon fontSize="small" />
                        {record?.userId ? (
                          <ReferenceField source="userId" reference="users" link={false}>
                            <FunctionField render={(userRecord: any) => (
                              <Typography variant="body2">{userRecord?.email || record?.email || '-'}</Typography>
                            )} />
                          </ReferenceField>
                        ) : (
                          <Typography variant="body2">{record?.email || '-'}</Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" />
                        {record?.userId ? (
                          <ReferenceField source="userId" reference="users" link={false}>
                            <FunctionField render={(userRecord: any) => (
                              <Typography variant="body2">{userRecord?.phone || record?.phone || '-'}</Typography>
                            )} />
                          </ReferenceField>
                        ) : (
                          <Typography variant="body2">{record?.phone || '-'}</Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>문의 내용</Typography>
                  <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                    <RichTextField source="content" />
                  </Paper>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>첨부파일</Typography>
                    <AttachmentsField />
                  </Box>
                </Box>

                {record?.answer && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                        관리자 답변
                      </Typography>
                      <Paper sx={{ p: 2, backgroundColor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
                        <RichTextField source="answer" />
                      </Paper>
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', fontSize: '0.875rem', color: 'text.secondary' }}>
                        <span>답변일: <DateField source="answeredAt" showTime /></span>
                      </Box>
                    </Box>
                  </>
                )}

                {/* 답변 작성 버튼 - 모바일에서 더 직관적으로 표시 */}
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                  <MuiButton
                    variant="contained"
                    color="primary"
                    startIcon={<ReplyIcon />}
                    onClick={() => setAnswerDialogOpen(true)}
                    fullWidth
                    size="large"
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                    }}
                  >
                    {record?.answer ? '답변 수정하기' : '답변 작성하기'}
                  </MuiButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>

        </Grid>

        <AnswerDialogWrapper
          open={answerDialogOpen}
          onClose={() => setAnswerDialogOpen(false)}
        />
      </SimpleShowLayout>
    </>
  );
};

// 문의 제목을 표시하는 컴포넌트
const InquiryTitle = () => {
  const record = useRecordContext();
  return <span>문의 상세</span>;
};

// 문의 상세 보기
export const InquiryShow = () => {
  const [answerDialogOpen, setAnswerDialogOpen] = useState(false);

  return (
    <Show
      title={<InquiryTitle />}
      actions={<TopToolbar />}
    >
      <InquiryShowContent
        answerDialogOpen={answerDialogOpen}
        setAnswerDialogOpen={setAnswerDialogOpen}
      />
    </Show>
  );
};

// InquiryEdit 컴포넌트는 제거됨 - 고객 문의 내용은 함부로 편집하면 안 됨
// 답변은 InquiryShow에서 답변 작성 버튼을 통해 가능